import { useEffect, useState } from "react";
import { CATALOG, newRoomId, type Movie } from "../catalog.ts";
import "./library.css";

interface Profile {
  name?: string;
  email?: string;
  picture?: string;
}

function readProfile(): Profile {
  try {
    return JSON.parse(localStorage.getItem("mov_profile") || "{}");
  } catch {
    return {};
  }
}

function startParty(movie: Movie) {
  const roomId = newRoomId();
  // Full navigation into the room; ?host=1 tells the room to pop the invite.
  window.location.search = `?room=${roomId}&movie=${encodeURIComponent(movie.id)}&host=1`;
}

export function Library() {
  const featured = CATALOG[0];
  const [detail, setDetail] = useState<Movie | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const profile = readProfile();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const signOut = () => {
    localStorage.removeItem("mov_onboarded");
    localStorage.removeItem("mov_profile");
    window.location.search = "?signup";
  };

  return (
    <div className="lib">
      <nav className={`lib-nav ${scrolled ? "is-solid" : ""}`}>
        <div className="lib-nav-left">
          <div className="lib-word">MOV</div>
          <a className="lib-link is-active">Home</a>
          <a className="lib-link">My List</a>
        </div>
        <div className="lib-nav-right">
          <button className="lib-icon" aria-label="Search">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" strokeLinecap="round" />
            </svg>
          </button>
          <div className="lib-profile" onClick={() => setMenuOpen((v) => !v)}>
            {profile.picture ? (
              <img src={profile.picture} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span className="lib-avatar-fallback">{(profile.name || "?").charAt(0).toUpperCase()}</span>
            )}
            {menuOpen && (
              <div className="lib-menu" onClick={(e) => e.stopPropagation()}>
                <div className="lib-menu-head">
                  <div className="nm">{profile.name || "You"}</div>
                  <div className="em">{profile.email}</div>
                </div>
                <button onClick={signOut}>Sign out</button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Billboard */}
      <header className="billboard">
        <div className="bb-art" style={{ backgroundImage: `url(${featured.hero})` }} />
        <div className="bb-fade" />
        <div className="bb-body">
          <div className="bb-kicker">
            <span className="mov-mark">M</span> FEATURED FILM
          </div>
          <h1 className="bb-title">{featured.title}</h1>
          <div className="bb-meta">
            <span className="rating">{featured.rating}</span>
            <span>{featured.year}</span>
            <span>{featured.durationLabel}</span>
            <span className="genres">{featured.genres.join(" · ")}</span>
          </div>
          <p className="bb-synopsis">{featured.synopsis}</p>
          <div className="bb-actions">
            <button className="play-btn" onClick={() => startParty(featured)}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              Start Watch Party
            </button>
            <button className="info-btn" onClick={() => setDetail(featured)}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" strokeLinecap="round" />
              </svg>
              More Info
            </button>
          </div>
        </div>
      </header>

      {/* Rows */}
      <section className="rows">
        <MovieRow title="Available on Mov" movies={CATALOG} onPick={setDetail} onPlay={startParty} />
        <MovieRow title="Because you're in the mood for a thriller" movies={CATALOG} onPick={setDetail} onPlay={startParty} />
      </section>

      {detail && <DetailModal movie={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function MovieRow({
  title,
  movies,
  onPick,
  onPlay,
}: {
  title: string;
  movies: Movie[];
  onPick: (m: Movie) => void;
  onPlay: (m: Movie) => void;
}) {
  return (
    <div className="row">
      <h2 className="row-title">{title}</h2>
      <div className="row-track">
        {movies.map((m) => (
          <button className="card" key={m.id} onClick={() => onPick(m)}>
            <div className="card-art" style={{ backgroundImage: `url(${m.card})` }}>
              <div className="card-hover">
                <span className="card-play" onClick={(e) => (e.stopPropagation(), onPlay(m))} role="button" aria-label="Start watch party">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </span>
              </div>
            </div>
            <div className="card-meta">
              <div className="card-title">{m.title}</div>
              <div className="card-sub">
                <span className="rating">{m.rating}</span> {m.year} · {m.durationLabel}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DetailModal({ movie, onClose }: { movie: Movie; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-x" onClick={onClose} aria-label="Close">×</button>
        <div className="modal-hero" style={{ backgroundImage: `url(${movie.hero})` }}>
          <div className="modal-hero-fade" />
          <div className="modal-hero-body">
            <h2 className="modal-title">{movie.title}</h2>
            <button className="play-btn" onClick={() => startParty(movie)}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              Start Watch Party
            </button>
          </div>
        </div>
        <div className="modal-body">
          <div className="modal-meta">
            <span className="rating">{movie.rating}</span>
            <span>{movie.year}</span>
            <span>{movie.durationLabel}</span>
            <span className="modal-tag">{movie.tagline}</span>
          </div>
          <p className="modal-synopsis">{movie.synopsis}</p>
          <div className="modal-genres">
            <span className="dim">Genres:</span> {movie.genres.join(", ")}
          </div>
        </div>
      </div>
    </div>
  );
}
